import { FC } from "react";
import { Button, Dialog, Flex, Stack, Text } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";

import { CASE_FILE_STATUS, CaseDocumentModel } from "@shared/models";
import { CheckIcon } from "@shared/components/Icons";
import { FONT_WEIGHT } from "@assets/theme/foundations/textStyle";
import { selectors as authSelectors } from "@containers/Auth/store";
import { deleteCaseFile, getCaseDocuments } from "@containers/Cases/store/actions";

import {
  CaseDocumentDoneIcon,
  CaseDocumentFailedFileRow,
  CaseDocumentFileRow,
  CaseDocumentModalHeader,
  CaseDocumentSavedLabel,
} from "../CaseDocumentModal";

interface CaseNotesResultStepProps {
  caseId: number;
  caseDocument: CaseDocumentModel;
  isFailed: boolean;
  /** The whole saved Case Notes field */
  caseNotesText: string | null;
  onClose: () => void;
  onReplaceFile: () => void;
}

const CaseNotesResultStep: FC<CaseNotesResultStepProps> = ({
  caseId,
  caseDocument,
  isFailed,
  caseNotesText,
  onClose,
  onReplaceFile,
}) => {
  const dispatch = useDispatch();
  // Changing case documents needs the update_case permission, which only admins have.
  const canChangeDocuments = !!useSelector(authSelectors.isAdmin());

  const deleteFile = (fileId: number) => {
    dispatch(deleteCaseFile.request({ caseId, fileId, cb: () => dispatch(getCaseDocuments.request(caseId)) }));
  };

  return (
    <>
      {isFailed ? (
        <CaseDocumentModalHeader
          title="Case notes couldn't be parsed"
          subtitle="Nothing was saved. Replace the file, or remove it."
        />
      ) : (
        <CaseDocumentModalHeader
          title="Case notes added"
          subtitle="The text was saved to section 07 · Case Notes."
          icon={<CaseDocumentDoneIcon />}
        />
      )}
      <Dialog.Body display="flex" flexDirection="column" gap={4}>
        <Stack gap={2}>
          {caseDocument.files.map((file) =>
            file.status === CASE_FILE_STATUS.FAILED ? (
              <CaseDocumentFailedFileRow
                key={file.id}
                file={file}
                title="Couldn't read this file"
                reason={file.error_message}
                onReplace={canChangeDocuments ? onReplaceFile : undefined}
                onRemove={canChangeDocuments ? () => deleteFile(file.id) : undefined}
              />
            ) : (
              <CaseDocumentFileRow key={file.id} file={file}>
                <CheckIcon boxSize={4} color="brand.checkMark" />
              </CaseDocumentFileRow>
            ),
          )}
        </Stack>

        {!isFailed && (
          <Flex
            direction="column"
            gap="6px"
            px="18px"
            py={4}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="10px"
          >
            <Flex align="center" justify="space-between" gap={2}>
              <Text fontSize="15px" fontWeight={FONT_WEIGHT.HEAVY} color="brand.modalHeaderText">
                Case Notes
              </Text>
              <CaseDocumentSavedLabel label="Saved as written" />
            </Flex>
            <Text
              maxH="240px"
              overflowY="auto"
              whiteSpace="pre-wrap"
              fontSize="13px"
              lineHeight={1.5}
              color="brand.textSecondary"
            >
              {caseNotesText}
            </Text>
          </Flex>
        )}
      </Dialog.Body>
      <Dialog.Footer gap={3} mt={2} borderTop="1px solid" borderColor="gray.200">
        <Button variant="primary" fontSize="14px" onClick={onClose}>
          Done
        </Button>
      </Dialog.Footer>
    </>
  );
};

export default CaseNotesResultStep;
